/**
 * Centralized Dialog Manager
 * Handles modals, drawers, and popups with proper accessibility
 * Consolidates duplicate implementations across the theme
 */
class DialogManager {
  constructor(element, options = {}) {
    this.dialog = element;
    this.options = {
      type: 'modal', // 'modal', 'drawer-left', 'drawer-right', 'popup'
      triggerElement: null,
      overlayElement: null,
      closeElements: [],
      onOpen: () => {},
      onClose: () => {},
      closeOnEscape: true,
      closeOnOverlayClick: true,
      preventBodyScroll: true,
      announceToScreenReaders: true,
      animationDuration: 300,
      focusFirstElement: true,
      inertBackgroundElements: true,
      // When true, opening this dialog will close other open dialogs.
      // When false, this dialog can stack on top without closing others.
      exclusiveOpen: true,
      ...options,
    };

    this.isOpen = false;
    this.previousActiveElement = null;
    this.focusableElements = [];
    this.firstFocusableElement = null;
    this.lastFocusableElement = null;

    this.init();
  }

  init() {
    // Set initial ARIA attributes
    this.dialog.setAttribute('role', 'dialog');
    this.dialog.setAttribute('aria-modal', 'true');
    this.dialog.setAttribute('aria-hidden', 'true');

    // Ensure dialog has inert attribute when closed
    if (this.options.inertBackgroundElements) {
      this.dialog.setAttribute('inert', '');
    }

    // Store original translate-y class for modal animations
    if (this.options.type === 'modal') {
      const innerElement = this.dialog.querySelector('[data-dialog-inner]');
      const animationElement = innerElement || this.dialog;

      // Look for transform element - try child first, then the element itself
      let transformElement = animationElement.querySelector('.transform');
      if (!transformElement && animationElement.classList.contains('transform')) {
        transformElement = animationElement;
      }

      if (transformElement) {
        // Find and store the original translate-y class
        const classList = Array.from(transformElement.classList);
        const translateYClass = classList.find((cls) => cls.startsWith('translate-y-'));
        if (translateYClass) {
          this.originalTranslateY = translateYClass;
        }
      }
    }

    // Set up trigger element
    if (this.options.triggerElement) {
      this.options.triggerElement.setAttribute('aria-haspopup', 'dialog');
      this.options.triggerElement.setAttribute('aria-expanded', 'false');

      this.options.triggerElement.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });

      // Handle keyboard activation
      this.options.triggerElement.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          this.toggle();
        }
      });
    }

    // Set up close elements
    this.options.closeElements.forEach((element) => {
      element.addEventListener('click', () => this.close());
    });

    // Set up overlay click
    if (this.options.overlayElement && this.options.closeOnOverlayClick) {
      this.options.overlayElement.addEventListener('click', () => this.close());
    }

    // Set up escape key
    if (this.options.closeOnEscape) {
      this.dialog.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });
    }
  }

  updateFocusableElements() {
    const focusableSelectors = [
      'button:not([disabled]):not([inert])',
      'input:not([disabled]):not([inert])',
      'textarea:not([disabled]):not([inert])',
      'select:not([disabled]):not([inert])',
      'a[href]:not([inert])',
      'details summary:not([inert])',
      '[tabindex]:not([tabindex="-1"]):not([inert])',
    ].join(', ');

    this.focusableElements = Array.from(this.dialog.querySelectorAll(focusableSelectors)).filter((el) => {
      const style = window.getComputedStyle(el);
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        el.offsetParent !== null &&
        !el.closest('[aria-hidden="true"]')
      );
    });

    this.firstFocusableElement = this.focusableElements[0];
    this.lastFocusableElement = this.focusableElements[this.focusableElements.length - 1];
  }

  trapFocus(e) {
    if (e.key !== 'Tab' || !this.isOpen) return;

    // Only the topmost dialog should handle focus trapping
    const topmost = DialogManager.getTopmostActiveDialog();
    if (topmost && topmost !== this) return;

    // Update focusable elements in case DOM changed
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) {
      e.preventDefault();
      return;
    }

    // Check if the currently focused element is within the dialog
    const isWithinDialog = this.dialog.contains(document.activeElement);
    const currentElement = document.activeElement;

    if (e.shiftKey) {
      // Shift + Tab (backwards)
      if (currentElement === this.firstFocusableElement || !isWithinDialog) {
        e.preventDefault();
        this.lastFocusableElement.focus();
      }
    } else {
      // Tab (forwards)
      if (currentElement === this.lastFocusableElement || !isWithinDialog) {
        e.preventDefault();
        this.firstFocusableElement.focus();
      }
    }
  }

  open() {
    if (this.isOpen) return;

    // Store current focus
    this.previousActiveElement = document.activeElement;

    // Close other dialogs only when opening exclusively
    if (this.options.exclusiveOpen) {
      DialogManager.closeAll(this);
    }

    // Remove inert attribute
    if (this.options.inertBackgroundElements) {
      this.dialog.removeAttribute('inert');
    }

    // Update ARIA
    this.dialog.setAttribute('aria-hidden', 'false');
    if (this.options.triggerElement) {
      this.options.triggerElement.setAttribute('aria-expanded', 'true');
    }

    // Add dialog classes based on type
    this.dialog.classList.remove('pointer-events-none');
    this.dialog.classList.add('pointer-events-auto');

    // Show overlay with animation
    if (this.options.overlayElement) {
      this.options.overlayElement.classList.remove('opacity-0', 'pointer-events-none');
      this.options.overlayElement.classList.add('opacity-100', 'pointer-events-auto');
    }

    // Type-specific animations
    const innerElement = this.dialog.querySelector('[data-dialog-inner]');
    const animationElement = innerElement || this.dialog; // Use dialog itself if no inner element

    if (animationElement) {
      switch (this.options.type) {
        case 'drawer-left':
          animationElement.classList.remove('-translate-x-full');
          animationElement.classList.add('shadow-xl');
          break;
        case 'drawer-right':
          animationElement.classList.remove('translate-x-full');
          animationElement.classList.add('shadow-xl');
          break;
        case 'popup':
          animationElement.classList.remove('scale-95', 'opacity-0');
          animationElement.classList.add('scale-100', 'opacity-100');
          break;
        case 'modal':
          animationElement.classList.remove('invisible', 'opacity-0', 'translate-y-8');
          animationElement.classList.add('visible', 'opacity-100', 'translate-y-0');
          break;
      }
    }

    // Prevent body scroll with reference counting
    if (this.options.preventBodyScroll) {
      if (DialogManager.bodyScrollLocks === 0) {
        document.body.classList.add('overflow-hidden');
      }
      DialogManager.bodyScrollLocks += 1;
    }

    // Set up focus management after animation
    setTimeout(() => {
      this.updateFocusableElements();

      // Focus first element or dialog itself
      if (this.options.focusFirstElement && this.firstFocusableElement) {
        this.firstFocusableElement.focus();
      } else {
        this.dialog.focus();
      }

      // Add focus trap to document to catch all tab events
      this.boundTrapFocus = this.trapFocus.bind(this);
      document.addEventListener('keydown', this.boundTrapFocus);
    }, 50);

    // Announce to screen readers
    if (this.options.announceToScreenReaders) {
      this.announce(`${this.dialog.getAttribute('aria-label') || 'Dialog'} opened`);
    }

    this.isOpen = true;
    this.options.onOpen();

    // Add to active dialogs
    DialogManager.activeDialogs.add(this);
  }

  close() {
    if (!this.isOpen) return;

    // Type-specific close animations
    const innerElement = this.dialog.querySelector('[data-dialog-inner]');
    const animationElement = innerElement || this.dialog; // Use dialog itself if no inner element

    if (animationElement) {
      switch (this.options.type) {
        case 'drawer-left':
          animationElement.classList.add('-translate-x-full');
          animationElement.classList.remove('shadow-xl');
          break;
        case 'drawer-right':
          animationElement.classList.add('translate-x-full');
          animationElement.classList.remove('shadow-xl');
          break;
        case 'popup':
          animationElement.classList.remove('scale-100', 'opacity-100');
          animationElement.classList.add('scale-95', 'opacity-0');
          break;
        case 'modal':
          animationElement.classList.remove('visible', 'opacity-100', 'translate-y-0');
          animationElement.classList.add('invisible', 'opacity-0', 'translate-y-8');
          break;
      }
    }

    // Hide overlay
    if (this.options.overlayElement) {
      this.options.overlayElement.classList.remove('opacity-100', 'pointer-events-auto');
      this.options.overlayElement.classList.add('opacity-0', 'pointer-events-none');
    }

    // Wait for animation before hiding
    setTimeout(() => {
      this.dialog.classList.remove('pointer-events-auto');
      this.dialog.classList.add('pointer-events-none');
      this.dialog.setAttribute('aria-hidden', 'true');

      // Add inert attribute
      if (this.options.inertBackgroundElements) {
        this.dialog.setAttribute('inert', '');
      }
    }, this.options.animationDuration);

    // Update ARIA on trigger
    if (this.options.triggerElement) {
      this.options.triggerElement.setAttribute('aria-expanded', 'false');
    }

    // Re-enable body scroll with reference counting
    if (this.options.preventBodyScroll) {
      DialogManager.bodyScrollLocks = Math.max(0, DialogManager.bodyScrollLocks - 1);
      if (DialogManager.bodyScrollLocks === 0) {
        document.body.classList.remove('overflow-hidden');
      }
    }

    // Remove focus trap
    if (this.boundTrapFocus) {
      document.removeEventListener('keydown', this.boundTrapFocus);
    }

    // Restore focus
    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }

    // Announce to screen readers
    if (this.options.announceToScreenReaders) {
      this.announce(`${this.dialog.getAttribute('aria-label') || 'Dialog'} closed`);
    }

    this.isOpen = false;
    this.options.onClose();

    // Remove from active dialogs
    DialogManager.activeDialogs.delete(this);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  announce(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'assertive');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }

  // Static methods
  static activeDialogs = new Set();
  static bodyScrollLocks = 0;

  static getTopmostActiveDialog() {
    // Convert Set to Array and get the last added dialog (most recent)
    const dialogsArray = Array.from(this.activeDialogs);
    return dialogsArray[dialogsArray.length - 1] || null;
  }

  static closeAll(except = null) {
    this.activeDialogs.forEach((dialog) => {
      if (dialog !== except) {
        dialog.close();
      }
    });
  }
}

// Export for use in other files
window.DialogManager = DialogManager;
